
import { useState } from "react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { BeakerIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function TestActions() {
  const { user, getAccessToken } = useAuth();
  const { useHasRoleQuery, useCreateTestNotificationMutation } = useUserRoles();
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  const { data: isTester } = useHasRoleQuery("TESTER");
  const createTestNotification = useCreateTestNotificationMutation();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [notificationType, setNotificationType] = useState<"reminder" | "system" | "info">("system");
  const [notificationTitle, setNotificationTitle] = useState("Test Notification");
  const [notificationMessage, setNotificationMessage] = useState("This is a test notification.");
  const [sendEmail, setSendEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendingState, setSendingState] = useState<"idle" | "sending" | "success" | "error">("idle");
  
  // Only show for developers or testers
  if (!isDeveloper && !isTester) {
    return null;
  }
  
  const handleSendTest = async () => {
    if (!user) return;
    
    setSendingState("sending");
    
    if (sendEmail) {
      // Send test email
      if (!recipientEmail) {
        toast.error("Please enter a recipient email address");
        setSendingState("error");
        return;
      }
      
      try {
        const accessToken = await getAccessToken();
        
        if (!accessToken) {
          toast.error("Authentication required");
          setSendingState("error");
          return;
        }
        
        console.log("Sending test email with token:", accessToken.substring(0, 10) + "...");
        
        const response = await fetch(`${SUPABASE_URL}/functions/v1/send-test-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            recipient: recipientEmail,
            subject: notificationTitle,
            message: notificationMessage
          })
        });
        
        const responseData = await response.json();
        
        if (!response.ok) {
          throw new Error(responseData.error || responseData.details || 'Failed to send test email');
        }
        
        toast.success(`Test email sent to ${recipientEmail}`);
        setSendingState("success");
        
        // Close dialog after successful send
        setTimeout(() => {
          setIsDialogOpen(false);
          setSendingState("idle");
        }, 1000);
      } catch (error) {
        console.error('Error sending test email:', error);
        toast.error(`Failed to send test email: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setSendingState("error");
      }
    } else {
      // Send in-app notification
      try {
        await createTestNotification.mutateAsync({
          userId: user.id,
          title: notificationTitle,
          message: notificationMessage,
          type: notificationType
        });
        
        setSendingState("success");
        
        // Close dialog after successful send
        setTimeout(() => {
          setIsDialogOpen(false);
          setSendingState("idle");
        }, 1000);
      } catch (error) {
        console.error('Error creating test notification:', error);
        setSendingState("error");
      }
    }
  };
  
  // Helper to determine button variant based on sending state
  const getButtonVariant = () => {
    switch (sendingState) {
      case "error":
        return "destructive";
      case "success":
        return "default";
      default:
        return "default";
    }
  };
  
  // Helper to determine button text based on sending state
  const getButtonText = () => {
    switch (sendingState) {
      case "sending":
        return "Sending...";
      case "success":
        return "Sent!";
      case "error":
        return "Failed to send!";
      default:
        return sendEmail ? "Send Test Email" : "Send Test Notification";
    }
  };
  
  return (
    <Dialog open={isDialogOpen} onOpenChange={(open) => {
      setIsDialogOpen(open);
      if (!open) {
        setSendingState("idle");
      }
    }}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex items-center gap-1"
        >
          <BeakerIcon className="h-4 w-4" />
          Test Notifications
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Test Notification</DialogTitle>
          <DialogDescription>
            Send a test notification or email to verify the system.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center space-x-2">
            <Switch 
              id="send-email" 
              checked={sendEmail} 
              onCheckedChange={setSendEmail} 
            />
            <Label htmlFor="send-email">Send as email instead of in-app notification</Label>
          </div>
          
          {sendEmail ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient Email</label>
              <Input 
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Notification Type</label>
              <Select 
                value={notificationType} 
                onValueChange={(value) => setNotificationType(value as "reminder" | "system" | "info")}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="space-y-2">
            <label className="text-sm font-medium">{sendEmail ? "Email Subject" : "Title"}</label>
            <Input 
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{sendEmail ? "Email Message" : "Message"}</label>
            <Input 
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            onClick={handleSendTest} 
            disabled={sendingState === "sending"}
            variant={getButtonVariant()}
          >
            {getButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
